import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

export default function AIMarketing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
      
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 relative z-10">
        
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <Link to="/blogs" className="inline-flex items-center text-sm font-body text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blogs
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white font-body border border-white/5">
              Marketing
            </span>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <Calendar className="w-3.5 h-3.5" />
              April 05, 2026
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <User className="w-3.5 h-3.5" />
              EsyAI Team
            </div>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6">
            AI-Powered Marketing Strategies That Drive Real Business Growth
          </h1>
          
          <p className="text-xl text-white/60 font-body font-light leading-relaxed">
            Discover how AI-powered marketing strategies can dramatically increase your visibility, leads, and conversions, and how EsyAI delivers results for UK businesses.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden mb-16 border border-white/10 liquid-glass shadow-2xl relative"
        >
          <img 
            src="/blogs/blog-4-new.png" 
            alt="AI Performance Metrics" 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        </motion.div>

        <motion.article 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="prose prose-invert prose-lg max-w-none font-body text-white/70 prose-headings:font-heading prose-headings:text-white prose-a:text-purple-400 hover:prose-a:text-purple-300 prose-p:leading-loose"
        >
          <p className="lead text-xl text-white/90">
            Marketing has changed more in the last three years than in the previous three decades. Artificial intelligence has fundamentally transformed how businesses reach, engage, and convert customers, and businesses that embrace AI-driven marketing are leaving their competitors behind.
          </p>
          
          <p>
            At EsyAI, we deliver intelligent, data-driven marketing strategies for businesses across the UK. Here's what modern AI-powered marketing looks like, and why it works.
          </p>

          <h2 className="text-2xl mt-12 mb-6">What Is AI-Powered Marketing?</h2>
          <p>
            AI-powered marketing means using artificial intelligence to analyse data, automate campaigns, personalise content, optimise ad spend, and surface insights that humans alone cannot process at scale. From SEO to social media to email, AI makes every channel smarter and more effective.
          </p>

          <h2 className="text-2xl mt-12 mb-6">1. SEO That Evolves with Google</h2>
          <p>
            Search engines are more sophisticated than ever, and so is the SEO needed to rank on them. AI tools now analyse thousands of ranking factors simultaneously, identify content gaps, and recommend optimisations that drive sustainable organic traffic.
          </p>
          <p>
            EsyAI builds SEO strategies that combine technical excellence with AI-driven content planning, ensuring your business ranks for the keywords your ideal customers are actually searching.
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>Keyword research powered by AI intent analysis</li>
            <li>Technical SEO audits and automated fixes</li>
            <li>Content strategies aligned to search demand</li>
            <li>Schema markup and structured data for rich results</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">2. Social Media Marketing That Connects</h2>
          <p>
            Social media is one of the most powerful channels for brand awareness and lead generation, when done strategically. AI tools analyse audience behaviour, identify peak engagement times, and test creative variations to maximise reach and ROI.
          </p>
          <p>
            Businesses using AI-informed social strategies see 2 to 3 times higher engagement rates compared to manual posting alone.
          </p>

          <h2 className="text-2xl mt-12 mb-6">3. Paid Advertising With Precision</h2>
          <p>
            Paid ads on Google, Meta, LinkedIn, and beyond are incredibly powerful when managed with AI. AI bidding algorithms, audience modelling, and creative testing ensure your budget is spent exactly where it delivers the highest return.
          </p>
          <p>
            EsyAI manages PPC campaigns with continuous AI optimisation, meaning your cost-per-lead decreases over time as the system learns what works.
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>Automated bid management for maximum ROI</li>
            <li>AI audience targeting and lookalike modelling</li>
            <li>Dynamic creative testing across demographics</li>
            <li>Real-time budget reallocation to top performers</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">4. Email Marketing That Feels Personal</h2>
          <p>
            AI transforms email marketing from a broadcast channel into a personalised, behaviour-triggered conversation. The right message, to the right person, at exactly the right moment. That's what AI-powered email delivers.
          </p>
          <p>
            We build automated email sequences that nurture leads, re-engage dormant customers, and drive repeat purchases, all without manual intervention.
          </p>

          <h2 className="text-2xl mt-12 mb-6">5. Content Marketing at Scale</h2>
          <p>
            Creating consistent, high-quality content is one of the biggest challenges for any business. AI-assisted content planning and creation allows EsyAI to help clients produce more content, faster, without sacrificing quality or authenticity.
          </p>
          <p>
            Blog posts, social captions, case studies, video scripts, all informed by data and designed to rank and convert.
          </p>

          <h2 className="text-2xl mt-12 mb-6">6. Analytics and Reporting That Drive Decisions</h2>
          <p>
            What gets measured gets improved. EsyAI sets up intelligent analytics dashboards that bring all your marketing data together, website traffic, ad performance, email metrics, social reach, into one clear, actionable view. No more spreadsheet chaos. Just clear insights that help you decide where to invest next.
          </p>

          <hr className="my-12 border-white/10" />

          <h2 className="text-2xl mb-6">The EsyAI Marketing Difference</h2>
          <p>
            We don't just run campaigns. We build systems. Every strategy we deliver is designed to compound over time. SEO traffic grows month on month, email sequences improve with every send, and paid ads become more efficient as AI learns your audience.
          </p>

          <div className="not-prose mt-12 p-8 liquid-glass rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-transparent">
            <h3 className="text-xl font-heading text-white mb-3">Ready to Grow?</h3>
            <p className="text-white/70 mb-6 text-base font-body">
              Whether you're starting from scratch or looking to dramatically improve your existing marketing, EsyAI has the expertise, tools, and AI-powered strategies to make it happen. Let's build something extraordinary together.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-full hover:bg-white/90 transition-colors font-body"
            >
              Get in touch <ArrowUpRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

        </motion.article>
      </div>
    </div>
  );
}
